import React, { useCallback, useEffect, useState } from "react";
import { axiosPost } from "../../../utils/AxiosApi";
import { checkWidgetAdminOrNot, headers, URL } from "../../../utils/Constants";
import DataTable from "react-data-table-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChalkboardTeacher,
  faBook,
  faDoorOpen,
  faLayerGroup,
  faArrowsRotate,
  faFilter,
} from "@fortawesome/free-solid-svg-icons";
import GranularPermissionModal from "../../Permissions/GranularPermissionModal";
import ModalWindow from "../../UI/ModalWindow";
import "./ListAllTeacher.scss";
import { Tooltip } from "@mui/material";
import Select from "../../../features/ARR/components/shared/Select";
import Pagination from "../../UI/Pagination";
import { usePagination } from "../../../hooks/usePagination";
import { Filter } from "../../../assets/icons/icon";
import FilterModal from "./FilterModal";
import { Spinner } from "reactstrap";
import swal from "sweetalert";
import axios from "axios";

// ─── Band pill ────────────────────────────────────────────────────────────────
const BandPill = ({ band }) => {
  const map = {
    Excellent: { bg: "#EAF3DE", color: "#3B6D11" },
    Good: { bg: "#E6F1FB", color: "#0C447C" },
    "Needs Improvement": { bg: "#FAEEDA", color: "#633806" },
    Critical: { bg: "#FCEBEB", color: "#791F1F" },
  };
  const s = map[band] || { bg: "#f1f5f9", color: "#64748b" };
  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        padding: "2px 9px",
        borderRadius: 99,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {band || "—"}
    </span>
  );
};

// ─── Mini progress bar ────────────────────────────────────────────────────────
export const MiniBar = ({
  value,
  total = 100,
  showPercent = true,
  floatValue = true,
}) => {
  const safeValue = Math.max(Number(value) || 0, 0);
  const pct = total > 0 ? Math.min((safeValue / total) * 100, 100) : 0;
  const color =
    pct >= 80
      ? "#3B6D11"
      : pct >= 65
        ? "#185FA5"
        : pct >= 45
          ? "#BA7517"
          : "#A32D2D";
  return (
    <Tooltip title={`Total: ${total}`}>
      <div
        style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 90 }}
      >
        <div
          style={{
            flex: 1,
            height: 5,
            borderRadius: 3,
            background: "#e2e8f0",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: "100%",
              background: color,
              borderRadius: 3,
            }}
          />
        </div>
        <span
          style={{
            fontSize: 12,
            color: "#64748b",
            minWidth: 36,
            // textAlign: "right",
          }}
        >
          {floatValue
            ? showPercent
              ? pct.toFixed(1)
              : safeValue.toFixed(1)
            : showPercent
              ? Math.round(pct)
              : safeValue}
          {showPercent && "%"}
        </span>
      </div>
    </Tooltip>
  );
};

// ─── Score range cell ─────────────────────────────────────────────────────────
const ScoreRange = ({ best, poorest }) => (
  <div
    style={{
      fontSize: 12,
      display: "flex",
      gap: 5,
      alignItems: "center",
      flexWrap: "nowrap",
    }}
  >
    <span style={{ color: "#3B6D11", fontWeight: 600, whiteSpace: "nowrap" }}>
      ↑{Number(best).toFixed(1)}%
    </span>
    <span style={{ color: "#94a3b8" }}>·</span>
    <span style={{ color: "#A32D2D", fontWeight: 600, whiteSpace: "nowrap" }}>
      ↓{Number(poorest).toFixed(1)}%
    </span>
  </div>
);

// ─── Tab config ───────────────────────────────────────────────────────────────
const TABS = [
  { value: "DETAIL", label: "Detail", icon: faLayerGroup },
  { value: "TEACHER", label: "Per teacher", icon: faChalkboardTeacher },
  { value: "SUBJECT", label: "Per subject", icon: faBook },
  { value: "CLASS", label: "Per class", icon: faDoorOpen },
  { value: "SECTION", label: "Per Section", icon: faDoorOpen },
];

// ─── Column definitions ───────────────────────────────────────────────────────
const buildColumns = (groupBy) => {
  const isDetail = groupBy === "DETAIL";
  const isTeacher = groupBy === "TEACHER";
  const isSubject = groupBy === "SUBJECT";
  const isClass = groupBy === "CLASS";
  const isSection = groupBy === "SECTION";

  const all = [
    // ── Identity ──────────────────────────────────────────────────────
    {
      id: "name",
      name: "Teacher",
      selector: (r) => r.pplName.trim(),
      sortable: true,
      minWidth: "150px",
      omit: isSubject || isClass || isSection,
      cell: (r) => (
        <div>
          <div style={{ fontWeight: 500, fontSize: 12 }}>
            {r?.pplName?.trim()}
          </div>
          {r.pplEmail && (
            <div style={{ fontSize: 12, color: "#94a3b8" }}>{r.pplEmail}</div>
          )}
        </div>
      ),
    },
    {
      id: "subject",
      name: "Subject",
      selector: (r) => r.subjectName,
      sortable: true,
      omit: isTeacher || isClass || isSection,
      cell: (r) => (
        <span style={{ fontSize: 12 }}>
          {r.subjectName || <span style={{ color: "#94a3b8" }}>Multiple</span>}
        </span>
      ),
    },
    {
      id: "class",
      name: "Class",
      selector: (r) => (isClass ? r.yearLevelId : r.classroomId),
      sortable: true,
      omit: isTeacher || isSubject,
      cell: (r) =>
        !!r.yearLevelId ? (
          <div>
            <div style={{ fontSize: 12 }}>{r.yearLevelName}</div>
            <div style={{ fontSize: 12, color: "#94a3b8" }}>
              {r.classroomName}
            </div>
          </div>
        ) : !!r.classroomId ? (
          <div>
            <div style={{ fontSize: 12 }}>{r.classroomName}</div>
            <div style={{ fontSize: 12, color: "#94a3b8" }}>
              {r.yearLevelName}
            </div>
          </div>
        ) : (
          <span style={{ color: "#94a3b8", fontSize: 12 }}>Multiple</span>
        ),
    },
    // Context counts for aggregated views
    {
      id: "scope",
      name: "Scope",
      sortable: false,
      omit: isDetail,
      cell: (r) => (
        <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.6 }}>
          {!isTeacher && r.distinctTeacherCount > 0 && (
            <div>
              {r.distinctTeacherCount} teacher
              {r.distinctTeacherCount > 1 ? "s" : ""}
            </div>
          )}
          {!isSubject && r.distinctSubjectCount > 0 && (
            <div>
              {r.distinctSubjectCount} subject
              {r.distinctSubjectCount > 1 ? "s" : ""}
            </div>
          )}
          {!isClass && r.distinctClassCount > 0 && (
            <div>
              {r.distinctClassCount} class{r.distinctClassCount > 1 ? "es" : ""}
            </div>
          )}
          {!isSection && r.distinctSectionCount > 0 && (
            <div>
              {r.distinctSectionCount} section
              {r.distinctSectionCount > 1 ? "s" : ""}
            </div>
          )}
        </div>
      ),
    },
    // ── Assessment & Student counts ───────────────────────────────────
    // {
    //   id: "assessments",
    //   name: "Assessments",
    //   selector: (r) => r.totalAssessmentCount,
    //   sortable: true,
    //   center: true,
    //   cell: (r) => (
    //     <span
    //       style={{
    //         background: "#f1f5f9",
    //         color: "#475569",
    //         borderRadius: 6,
    //         padding: "2px 8px",
    //         fontSize: 12,
    //       }}
    //     >
    //       {r.totalAssessmentCount}
    //     </span>
    //   ),
    // },
    {
      id: "students",
      name: "Students",
      selector: (r) => r.totalStudentsCount,
      sortable: true,
      cell: (r) => (
        <div style={{ fontSize: 12 }}>
          <div>{r.totalStudentsCount} total</div>
          <div style={{ color: "#94a3b8" }}>{r.absentStudents} absent</div>
        </div>
      ),
    },
    {
      id: "participants",
      name: "Participants",
      selector: (r) => r.totalParticipantStudents,
      sortable: true,
      cell: (r) => (
        <div style={{ fontSize: 12 }}>
          <div>{r.totalParticipantStudents}</div>
          <div style={{ color: "#94a3b8" }}>
            {r.passStudentsCount} pass · {r.failStudentsCount} fail
          </div>
        </div>
      ),
    },
    // ── Rate metrics ──────────────────────────────────────────────────
    {
      id: "participationRate",
      name: "Participation",
      selector: (r) => r.participationRate,
      sortable: true,
      cell: (r) => <MiniBar value={r.participationRate} />,
    },
    {
      id: "passRate",
      name: "Pass rate",
      selector: (r) => r.passRate,
      sortable: true,
      cell: (r) => <MiniBar value={r.passRate} />,
    },
    {
      id: "avgScore",
      name: "Avg score",
      selector: (r) => r.studentsAveragePercentage,
      sortable: true,
      cell: (r) => <MiniBar value={r.studentsAveragePercentage} />,
    },
    {
      id: "avgMarks",
      name: "Avg marks",
      selector: (r) => r.subjectAverageMarks,
      sortable: true,
      cell: (r) => (
        <span style={{ fontSize: 12 }}>
          {Number(r.subjectAverageMarks).toFixed(2)}
        </span>
      ),
    },
    {
      id: "range",
      name: "Score range",
      sortable: false,
      cell: (r) => (
        <ScoreRange
          best={r.studentsBestObtainPercentage}
          poorest={r.studentsPoorestObtainPercentage}
        />
      ),
    },
    // {
    //   id: "markRange",
    //   name: "Spread",
    //   selector: (r) => r.markRange,
    //   sortable: true,
    //   width: "80px",
    //   cell: (r) => (
    //     <span style={{ fontSize: 12, color: "#64748b" }}>
    //       ±{Number(r.markRange).toFixed(1)}%
    //     </span>
    //   ),
    // },
    // // ── Performance band ──────────────────────────────────────────────
    // {
    //   id: "band",
    //   name: "Band",
    //   selector: (r) => r.performanceBand,
    //   sortable: true,
    //   cell: (r) => <BandPill band={r.performanceBand} />,
    // },
  ];

  return all.filter((c) => !c.omit);
};

// ─── Custom styles for DataTable ──────────────────────────────────────────────
const tableStyles = {
  headCells: {
    style: {
      fontWeight: 600,
      fontSize: "13px",
      textTransform: "uppercase",
      color: "#000000de",
      background: "#fff",
      paddingTop: 10,
      paddingBottom: 10,
    },
  },
  rows: {
    style: {
      fontSize: "0.875rem",
      "&:hover": { background: "#f8fafc" },
    },
  },
  cells: {
    style: { paddingTop: 8, paddingBottom: 8 },
  },
};

// ─── Main component ───────────────────────────────────────────────────────────
const ListAllTeachers = ({ teachers }) => {
  const [listTeachers, setListTeachers] = useState(teachers || []);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [groupBy, setGroupBy] = useState("DETAIL");
  const [permissionModal, setPermissionModal] = useState(false);
  const [filterModal, setFilterModal] = useState(false);
  const [filterData, setFilterData] = useState({
    assessmentModuleCode: "",
    educationLevelId: "",
    assessmentId: "",
    // participants: [0, 100],
    // passrate: [0, 100],
    // avgScore: [0, 100],
    // avgMarks: "",
  });
  const [etlLoading, setEtlLoading] = useState(false);
  const [totalRecordsCount, setTotalRecordsCount] = useState(0);
  const {
    currentPage,
    pageSize,
    totalPages,
    onPageChange,
    onNextPage,
    onPrevPage,
  } = usePagination();

  const getAllTeachers = (page) => {
    setLoading(true);
    return new Promise((resolve, reject) => {
      axiosPost(
        URL.getPeopleForEvaluation,
        {
          groupBy,
          limit: pageSize,
          offset: page - 1 ?? 0,
          assessmentId: filterData?.assessmentId ?? null,
        },
        (response) => {
          if (response.status === 200) {
            setListTeachers(response.data.data);
            setTotalRecordsCount(
              response?.data?.totalRecordsCount ?? response?.data?.data?.length,
            );
            resolve(response);
          } else {
            reject(response);
          }
          setLoading(false);
        },
        (error) => {
          setLoading(false);
          reject(error);
        },
      );
    });
  };

  const refreshEtl = () => {
    setEtlLoading(true);
    return new Promise((resolve, reject) => {
      axiosPost(
        URL.refreshEtl,
        {},
        (response) => {
          setEtlLoading(false);
          if (response.status === 200) {
            resolve(response);
          } else {
            reject(response);
          }
        },
        (error) => {
          setEtlLoading(false);
          reject(error);
        },
      );
    });
  };

  const handleRefreshClick = async () => {
    try {
      await refreshEtl();
      await getAllTeachers();
    } catch (error) {
      console.log("Batch Processing Failed", error);
    }
  };

  const handleFilter = async () => {
    try {
      await getAllTeachers();
      setFilterModal(false);
    } catch (error) {
      console.error("Failed to get teachers data", error);
    }
  };

  const handleExport = async () => {
    setExporting(true);

    try {
      const response = await axios.post(
        URL.exportTeachersData,
        { groupBy, assessmentId: filterData?.assessmentId ?? null },
        {
          headers: headers,
          responseType: "blob",
        },
      );

      if (response?.status === 200) {
        const url = window.URL.createObjectURL(response?.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = "teachers_performance.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }

      setExporting(false);
    } catch (error) {
      swal("Oops", "Failed to export teachers data");
      setExporting(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await getAllTeachers(currentPage);
      } catch (error) {
        console.error("Failed to get teachers data", error);
      }
    })();
  }, [groupBy]);

  const columns = buildColumns(groupBy);

  return (
    <>
      <div className="arr-content">
        {/* ── Header ── */}
        <div className="header flex" style={{ marginBottom: "24px" }}>
          <div
            className="flex header-btns"
            style={{
              gap: 8,
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap-reverse",
              width: "100%",
            }}
          >
            {/* Group-by tabs */}
            <Select
              options={TABS}
              value={TABS?.find((opt) => opt.value === groupBy)}
              onChange={(option) => setGroupBy(option?.value)}
              styles={{ control: { width: "230px" } }}
              isClearable={false}
            />

            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Tooltip title={"Filter"}>
                <button
                  style={{ padding: "8px 8px" }}
                  className="ghost-btn"
                  onClick={() => setFilterModal((prev) => !prev)}
                >
                  <Filter width={20} height={20} />
                </button>
              </Tooltip>

              {checkWidgetAdminOrNot("Learning Achievement") && (
                <>
                  <Tooltip title={"Refresh"}>
                    <button
                      style={{ padding: "8px 8px" }}
                      className="ghost-btn"
                      onClick={handleRefreshClick}
                    >
                      <FontAwesomeIcon
                        className={etlLoading ? "refresh-icon" : ""}
                        icon={faArrowsRotate}
                        fontSize={16}
                      />
                    </button>
                  </Tooltip>
                </>
              )}

              <button
                style={{ padding: "8px 8px" }}
                className="grayOutline-btn"
                onClick={handleExport}
              >
                {exporting ? <Spinner size={"sm"} color="primary" /> : "Export"}
              </button>

              {checkWidgetAdminOrNot("Learning Achievement") && (
                <>
                  <button
                    className="grayOutline-btn"
                    onClick={() => setPermissionModal((prev) => !prev)}
                  >
                    Permission
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Table ── */}
        <div
          className="arr-table-container"
          style={{
            width: "1200px",
            minWidth: "100%",
            maxWidth: "100%",
          }}
        >
          <DataTable
            columns={columns}
            data={listTeachers}
            // pagination
            // paginationPerPage={10}
            // paginationRowsPerPageOptions={[10, 20, 50]}
            progressPending={loading}
            progressComponent={
              <div style={{ padding: 32, color: "#94a3b8", fontSize: 13 }}>
                Loading...
              </div>
            }
            noDataComponent={
              <div style={{ padding: 32, color: "#94a3b8", fontSize: 13 }}>
                No records found for this grouping.
              </div>
            }
            defaultSortFieldId="avgScore"
            defaultSortAsc={false}
            highlightOnHover
            striped
            responsive
            customStyles={tableStyles}
          />
        </div>

        {/* <div
          className="modern-pagination"
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "48px",
          }}
        >
          <Pagination
            type="modern"
            itemsCount={20}
            pageSize={pageSize}
            currentPage={currentPage}
            onPageChange={onPageChange}
            nextPage={onNextPage}
            previousPage={onPrevPage}
          />
        </div> */}
      </div>

      {/* Filter */}
      <FilterModal
        filterModal={filterModal}
        filterData={filterData}
        setFilterData={setFilterData}
        setFilterModal={setFilterModal}
        handleFilter={handleFilter}
        type={"teacher"}
      />

      {/* ── Permission modal ── */}
      <ModalWindow
        modal={permissionModal}
        toggleModal={() => setPermissionModal((prev) => !prev)}
        modalHeader="Assign permission to user"
        size="lg"
        id="tt-permissionModal"
        modalBody={
          <GranularPermissionModal
            widgetName="Learning Achievement"
            moduleName="Learning Achievement"
            header="List Teachers"
            activityName="teacher_achievement"
          />
        }
      />
    </>
  );
};

export default ListAllTeachers;
